/**
 * Opening semantics and frame math — a faithful port of
 * `realassets-exp/floorplan/openings.py` (normalization) and the `_opening_frame`
 * / `_sym_*` family of `json_to_svg.py`, which the backend already ports in
 * `internal/svgplan/openings.go`. Editor and server renderer therefore draw the
 * same door from the same numbers (spec 2026-08-22-02 §8).
 *
 * An opening is described by four *orthogonal* attributes:
 *
 *   kind       what it is                door | window
 *   operation  how the leaf moves        hinged | sliding | pocket | folding | fixed | none
 *   swing      which END along the wall  left | right
 *              the leaf is anchored to
 *   leaves     how many panels           1 | 2
 *
 * plus `rotation` (0/180), which picks the SIDE of the wall the fixture sits
 * on. All of it is folded into the frame once, so no symbol re-derives it.
 */
import { fnum, normalizeDeg, toRad, type Point } from './geometry'
import { PLAN_COLORS } from './palette'

export type Operation = 'hinged' | 'sliding' | 'pocket' | 'folding' | 'fixed' | 'none'
export type Swing = 'left' | 'right'

export const OPERATIONS: Operation[] = ['hinged', 'sliding', 'pocket', 'folding', 'fixed', 'none']
export const SWINGS: Swing[] = ['left', 'right']
/** Operations whose leaf is anchored to one end, so `swing` (and ⇄ flip) means something. */
export const DIRECTIONAL_OPERATIONS: Operation[] = ['hinged', 'sliding', 'pocket', 'folding']

export const DEFAULT_DOOR_WIDTH = 800
export const DEFAULT_WINDOW_WIDTH = 1200
export const DEFAULT_WALL_THICKNESS = 100
/** The erasing void overshoots the wall faces so no hairline of wall survives. */
const VOID_OVERSHOOT = 20 // mm

const OPERATION_ALIASES: Record<string, Operation> = {
    swinging: 'hinged', swing: 'hinged', hinge: 'hinged', casement: 'hinged',
    battant: 'hinged', battante: 'hinged',
    slide: 'sliding', slider: 'sliding', coulissant: 'sliding',
    coulissante: 'sliding', sliding_door: 'sliding',
    galandage: 'pocket', pocket_sliding: 'pocket', recessed: 'pocket',
    bifold: 'folding', 'bi-fold': 'folding', accordion: 'folding',
    concertina: 'folding', pliante: 'folding',
    glazed: 'fixed', static: 'fixed',
    open: 'none', opening: 'none', cased: 'none', passage: 'none', doorway: 'none',
}

/** Legacy `swing` values that were really operations / leaf counts. */
const LEGACY_SWING: Record<string, { operation: Operation | ''; leaves: number }> = {
    sliding: { operation: 'sliding', leaves: 1 },
    none: { operation: 'none', leaves: 1 },
    both: { operation: '', leaves: 2 },
}

export type OpeningKind = 'door' | 'window'

export const isWindowKind = (kind: string | undefined): boolean =>
    String(kind ?? '').trim().toLowerCase() === 'window'

export const defaultOperation = (kind: string | undefined): Operation =>
    (isWindowKind(kind) ? 'fixed' : 'hinged')

export const defaultWidth = (kind: string | undefined): number =>
    (isWindowKind(kind) ? DEFAULT_WINDOW_WIDTH : DEFAULT_DOOR_WIDTH)

/** The minimal shape the opening math needs — a subset of `OpeningElement`. */
export interface OpeningLike {
    openingKind: OpeningKind
    center: Point
    directionDeg: number
    width: number
    wallThickness?: number
    operation?: Operation
    swing?: Swing
    leaves?: 1 | 2
    rotation?: 0 | 180
}

export interface OpeningSemantics {
    operation: Operation
    swing: Swing | ''
    leaves: 1 | 2
}

/**
 * Migrates legacy spellings and drops values that aren't representable, without
 * mutating the input. Defaults stay implicit — a door is `hinged`, a window is
 * `fixed`, both have one leaf.
 */
export function normalizeOpeningSemantics(o: {
    openingKind?: string
    operation?: string
    swing?: string
    leaves?: number
}): OpeningSemantics {
    let op = String(o.operation ?? '').trim().toLowerCase()
    op = OPERATION_ALIASES[op] ?? op
    let leaves = o.leaves
    let swing = String(o.swing ?? '').trim().toLowerCase()

    const legacy = LEGACY_SWING[swing]
    if (legacy) {
        if (legacy.operation && !op) op = legacy.operation
        if (legacy.leaves === 2 && !leaves) leaves = 2
        swing = ''
    }
    if (!OPERATIONS.includes(op as Operation)) op = defaultOperation(o.openingKind)
    const operation = op as Operation
    const resolvedLeaves: 1 | 2 = Number(leaves) === 2 ? 2 : 1
    let resolvedSwing: Swing | '' = swing === 'left' || swing === 'right' ? (swing as Swing) : ''
    if (resolvedSwing && !DIRECTIONAL_OPERATIONS.includes(operation)) resolvedSwing = ''
    return { operation, swing: resolvedSwing, leaves: resolvedLeaves }
}

/** The resolved operation of an opening (never empty). */
export const operationOf = (o: { openingKind?: string; operation?: string }): Operation =>
    normalizeOpeningSemantics(o).operation

export interface OpeningFrame {
    cx: number
    cy: number
    ux: number
    uy: number
    nx: number
    ny: number
    width: number
    halfW: number
    thickness: number
    halfT: number
    /** Plan units per millimetre: the symbols' millimetre stroke floors go through it. */
    mm: number
    /** +1 / -1 — folds `swing` into the along-wall axis. */
    sign: number
    /** +1 / -1 — folds `rotation` (which wall face) into the across-wall axis. */
    side: number
    sweep: 0 | 1
    /** (along, across) → absolute point in the representation's local frame. */
    pt: (du: number, dn: number) => Point
    /** The erasing void, built in raw wall coordinates so its order is stable. */
    voidPoints: Point[]
}

/**
 * Resolves an opening's local drawing frame. `du` runs along the wall from the
 * anchored end (-halfW) to the far end (+halfW); `dn` runs across the wall
 * towards the side the fixture opens onto.
 */
export function openingFrame(o: OpeningLike, unitsPerMm: number = 1): OpeningFrame {
    // The fallbacks are millimetre defaults; `unitsPerMm` puts them in plan units.
    const width = o.width > 0 ? o.width : defaultWidth(o.openingKind) * unitsPerMm
    const halfW = width / 2
    const rad = toRad(o.directionDeg)
    const ux = Math.cos(rad)
    const uy = Math.sin(rad)
    const nx = -uy
    const ny = ux
    const thickness = o.wallThickness && o.wallThickness > 0 ? o.wallThickness : DEFAULT_WALL_THICKNESS * unitsPerMm
    const halfT = thickness / 2 + VOID_OVERSHOOT * unitsPerMm

    const rot = normalizeDeg(o.rotation ?? 0)
    const side = rot >= 90 && rot < 270 ? -1 : 1
    const sem = normalizeOpeningSemantics(o)
    const sign = sem.swing === 'left' ? -side : side
    const sweep: 0 | 1 = sign * side > 0 ? 1 : 0

    const cx = o.center.x
    const cy = o.center.y
    const pt = (du: number, dn: number): Point => ({
        x: cx + ux * du * sign + nx * dn * side,
        y: cy + uy * du * sign + ny * dn * side,
    })
    const voidPoints = ([[-halfW, -halfT], [halfW, -halfT], [halfW, halfT], [-halfW, halfT]] as const)
        .map(([du, dn]) => ({ x: cx + ux * du + nx * dn, y: cy + uy * du + ny * dn }))

    return { cx, cy, ux, uy, nx, ny, width, halfW, thickness, halfT, mm: unitsPerMm, sign, side, sweep, pt, voidPoints }
}

/* ------------------------------------------------------------------ symbols */

const seg = (a: Point, b: Point, color: string, stroke: number, dash?: string): string =>
    `<line x1="${fnum(a.x)}" y1="${fnum(a.y)}" x2="${fnum(b.x)}" y2="${fnum(b.y)}" ` +
    `stroke="${color}" stroke-width="${fnum(stroke)}" fill="none"` +
    (dash ? ` stroke-dasharray="${dash}"` : '') + '/>'

function symHinged(f: OpeningFrame, leaves: number, leafColor: string, arcColor: string): string {
    const leafStroke = Math.max(f.thickness * 0.25, 20 * f.mm)
    const arcStroke = Math.max(f.thickness * 0.15, 12 * f.mm)
    const dash = `${fnum(f.width * 0.08)},${fnum(f.width * 0.06)}`
    const arc = (from: Point, to: Point, r: number, sw: number): string =>
        `<path d="M ${fnum(from.x)} ${fnum(from.y)} A ${fnum(r)} ${fnum(r)} 0 0 ${sw} ${fnum(to.x)} ${fnum(to.y)}" ` +
        `stroke="${arcColor}" stroke-width="${fnum(arcStroke)}" fill="none" stroke-dasharray="${dash}"/>`
    const hw = f.halfW
    if (leaves >= 2) {
        return (
            seg(f.pt(-hw, 0), f.pt(0, 0), leafColor, leafStroke) +
            arc(f.pt(0, 0), f.pt(-hw, hw), hw, f.sweep) +
            seg(f.pt(hw, 0), f.pt(0, 0), leafColor, leafStroke) +
            arc(f.pt(0, 0), f.pt(hw, hw), hw, 1 - f.sweep)
        )
    }
    return (
        seg(f.pt(-hw, 0), f.pt(hw, 0), leafColor, leafStroke) +
        arc(f.pt(hw, 0), f.pt(-hw, f.width), f.width, f.sweep)
    )
}

function arrow(f: OpeningFrame, duFrom: number, duTo: number, dn: number, color: string, stroke: number): string {
    const head = f.width * 0.09
    const back = duTo + (duTo > duFrom ? head : -head)
    const tip = f.pt(duTo, dn)
    return (
        seg(f.pt(duFrom, dn), tip, color, stroke) +
        seg(tip, f.pt(back, dn - head * 0.6), color, stroke) +
        seg(tip, f.pt(back, dn + head * 0.6), color, stroke)
    )
}

function symSliding(f: OpeningFrame, pocket: boolean, leaves: number): string {
    const panel = Math.max(f.thickness * 0.22, 24 * f.mm)
    const track = Math.max(f.thickness * 0.08, 8 * f.mm)
    const dash = `${fnum(f.width * 0.05)},${fnum(f.width * 0.04)}`
    const dn = pocket ? 0 : f.thickness / 2 + panel * 0.75
    const tc = pocket ? PLAN_COLORS.voidFill : PLAN_COLORS.doorSwing
    const hw = f.halfW
    if (leaves >= 2) {
        const dn2 = pocket ? dn : dn + panel
        return (
            seg(f.pt(-hw, dn), f.pt(0, dn), PLAN_COLORS.doorLeaf, panel) +
            seg(f.pt(0, dn2), f.pt(hw, dn2), PLAN_COLORS.doorLeaf, panel) +
            seg(f.pt(-hw, dn), f.pt(-hw * 1.9, dn), tc, track, dash) +
            seg(f.pt(hw, dn2), f.pt(hw * 1.9, dn2), tc, track, dash) +
            arrow(f, -hw * 0.25, -hw * 1.5, dn, tc, track) +
            arrow(f, hw * 0.25, hw * 1.5, dn2, tc, track)
        )
    }
    return (
        seg(f.pt(-hw, dn), f.pt(hw, dn), PLAN_COLORS.doorLeaf, panel) +
        seg(f.pt(-hw, dn), f.pt(-hw - f.width * 0.9, dn), tc, track, dash) +
        arrow(f, -hw * 0.15, -hw - f.width * 0.55, dn, tc, track)
    )
}

/** A concertina of four panels folding back to the anchored end. */
function symFolding(f: OpeningFrame): string {
    const stroke = Math.max(f.thickness * 0.2, 16 * f.mm)
    const depth = f.width * 0.22
    const q = f.width / 4
    const pts = [0, 1, 2, 3, 4].map((i) => f.pt(-f.halfW + i * q, i % 2 ? depth : 0))
    return pts.slice(1).map((p, i) => seg(pts[i], p, PLAN_COLORS.doorLeaf, stroke)).join('')
}

/** A cased opening — no leaf at all, jamb ticks only. */
function symCased(f: OpeningFrame): string {
    const stroke = Math.max(f.thickness * 0.08, 8 * f.mm)
    const h = f.halfT - VOID_OVERSHOOT * f.mm
    return seg(f.pt(-f.halfW, -h), f.pt(-f.halfW, h), PLAN_COLORS.jamb, stroke) +
        seg(f.pt(f.halfW, -h), f.pt(f.halfW, h), PLAN_COLORS.jamb, stroke)
}

function symGlazing(f: OpeningFrame, sem: OpeningSemantics): string {
    const rail = Math.max(f.thickness * 0.18, 24 * f.mm)
    const pane = Math.max(f.thickness * 0.08, 8 * f.mm)
    const q = f.thickness / 4
    const hw = f.halfW
    const rails = seg(f.pt(-hw, -q), f.pt(hw, -q), PLAN_COLORS.glazing, rail) +
        seg(f.pt(-hw, q), f.pt(hw, q), PLAN_COLORS.glazing, rail)
    if (sem.operation === 'sliding' || sem.operation === 'pocket') {
        const lap = f.width * 0.06
        return rails +
            seg(f.pt(-hw, -q), f.pt(lap, -q), PLAN_COLORS.glazing, pane * 2) +
            seg(f.pt(-lap, q), f.pt(hw, q), PLAN_COLORS.glazing, pane * 2) +
            arrow(f, lap, -hw * 0.85, -q, PLAN_COLORS.glazingLight, pane)
    }
    if (sem.operation === 'folding') return rails + symFolding(f)
    if (sem.operation === 'hinged') return rails + symHinged(f, 1, PLAN_COLORS.glazing, PLAN_COLORS.glazingLight)
    if (sem.operation === 'none') return symCased(f)
    return rails + seg(f.pt(-hw, 0), f.pt(hw, 0), PLAN_COLORS.glazingLight, pane)
}

/** The operation symbol of one opening — everything but the erasing void. */
export function openingSymbol(o: OpeningLike, unitsPerMm: number = 1): string {
    const f = openingFrame(o, unitsPerMm)
    const sem = normalizeOpeningSemantics(o)
    if (isWindowKind(o.openingKind)) return symGlazing(f, sem)
    switch (sem.operation) {
        case 'sliding':
        case 'pocket':
            return symSliding(f, sem.operation === 'pocket', sem.leaves)
        case 'folding':
            return symFolding(f)
        case 'none':
            return symCased(f)
        case 'fixed':
            // A door that does not move: leaf only, no swing arc.
            return seg(f.pt(-f.halfW, 0), f.pt(f.halfW, 0), PLAN_COLORS.doorLeaf, Math.max(f.thickness * 0.25, 20 * f.mm))
        default:
            return symHinged(f, sem.leaves, PLAN_COLORS.doorLeaf, PLAN_COLORS.doorSwing)
    }
}

/**
 * ⇄ flip side: `rotation + 180` point-rotates the fixture (anchored end AND
 * wall side both flip), and toggling `swing` restores the anchored end — so
 * only the wall side changes, which is what the control promises.
 */
export function flipOpeningSide<T extends { rotation?: 0 | 180; swing?: Swing }>(o: T): T {
    o.rotation = (normalizeDeg((o.rotation ?? 0) + 180) === 180 ? 180 : 0) as 0 | 180
    o.swing = o.swing === 'left' ? 'right' : 'left'
    return o
}
